import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Edit2, Upload, CheckCircle2, AlertCircle, FileUp } from 'lucide-react';
import axios from 'axios';

const TransactionActions = ({
  deal,
  selectedTransactions,
  isAdmin,
  getAuthHeaders,
  onBulkDelete,
  onOpenMoveDialog,
  onBulkEdit,
  onDownloadCSV,
  onUploadComplete,
}) => {
  const fileInputRef = useRef(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditType, setBulkEditType] = useState('');
  const [bulkEditStatus, setBulkEditStatus] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadResult(null);
    setUploadError(null);
    setUploading(true);
    setUploadOpen(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const resp = await axios.post(
        `${API_URL}/api/deals/${deal.id}/upload-transactions`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      setUploadResult(resp.data);
      if (resp.data.inserted > 0 && onUploadComplete) onUploadComplete();
    } catch (err) {
      setUploadError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleBulkEdit = () => {
    const updates = {};
    if (bulkEditType) updates.type = bulkEditType;
    if (bulkEditStatus) updates.status = bulkEditStatus;
    if (Object.keys(updates).length > 0 && onBulkEdit) onBulkEdit(selectedTransactions, updates);
    setBulkEditOpen(false);
    setBulkEditType('');
    setBulkEditStatus('');
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={onDownloadCSV} data-testid="export-csv-btn">Export CSV</Button>

      {isAdmin && (
        <>
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" data-testid="csv-file-input" onChange={handleCsvUpload} />
          <Button size="sm" variant="outline" data-testid="upload-csv-btn" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />Upload CSV
          </Button>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><FileUp className="h-5 w-5" />CSV Upload</DialogTitle>
                <DialogDescription>Transaction import results</DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                {uploading && (
                  <div className="flex items-center gap-3" data-testid="upload-progress">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="text-sm text-muted-foreground">Uploading and processing...</span>
                  </div>
                )}
                {uploadError && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-red-950/40 border border-red-800" data-testid="upload-error">
                    <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-300">{uploadError}</p>
                  </div>
                )}
                {uploadResult && (
                  <div className="space-y-3" data-testid="upload-result">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-green-950/40 border border-green-800">
                      <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 shrink-0" />
                      <div className="text-sm space-y-1">
                        <p className="font-medium text-green-300">Upload complete</p>
                        <p className="text-muted-foreground">{uploadResult.total_rows} rows processed</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <p className="text-2xl font-bold text-green-400" data-testid="upload-inserted">{uploadResult.inserted}</p>
                        <p className="text-xs text-muted-foreground">Inserted</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <p className="text-2xl font-bold text-yellow-400" data-testid="upload-skipped">{uploadResult.skipped}</p>
                        <p className="text-xs text-muted-foreground">Skipped</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <p className="text-2xl font-bold text-red-400" data-testid="upload-errors">{uploadResult.validation_errors}</p>
                        <p className="text-xs text-muted-foreground">Errors</p>
                      </div>
                    </div>
                    {uploadResult.skipped_rows?.length > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">{uploadResult.skipped} skipped row(s)</summary>
                        <ul className="mt-2 space-y-1 pl-4 text-muted-foreground">
                          {uploadResult.skipped_rows.map((r, i) => <li key={`skip-${r.row}-${i}`}>Row {r.row}: {r.reason}</li>)}
                        </ul>
                      </details>
                    )}
                    {uploadResult.validation_error_rows?.length > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">{uploadResult.validation_errors} validation error(s)</summary>
                        <ul className="mt-2 space-y-1 pl-4 text-muted-foreground">
                          {uploadResult.validation_error_rows.map((r, i) => <li key={`err-${r.row}-${i}`}>Row {r.row}: {r.reason}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setUploadOpen(false); setUploadResult(null); setUploadError(null); }}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {selectedTransactions.length > 0 && (
        <>
          {onBulkDelete && <Button size="sm" variant="destructive" onClick={onBulkDelete}>Delete Selected ({selectedTransactions.length})</Button>}
          <Button size="sm" variant="outline" onClick={onOpenMoveDialog}>Move Selected</Button>
          <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline"><Edit2 className="h-4 w-4 mr-2" />Bulk Edit ({selectedTransactions.length})</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Edit {selectedTransactions.length} Transaction(s)</DialogTitle>
                <DialogDescription>Update multiple transactions at once. Only changed fields will be updated.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Change Type To</Label>
                  <Select value={bulkEditType} onValueChange={setBulkEditType}>
                    <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAYMENT">Payment</SelectItem>
                      <SelectItem value="ACH_RETURN">ACH Return</SelectItem>
                      <SelectItem value="BOUNCE_FEE">Bounce Fee</SelectItem>
                      <SelectItem value="DEFAULT_FEE">Default Fee</SelectItem>
                      <SelectItem value="DEFERRED_FEE">Deferred Fee</SelectItem>
                      <SelectItem value="REFUND">Refund</SelectItem>
                      <SelectItem value="DISCOUNT">Discount</SelectItem>
                      <SelectItem value="FEE_DISCOUNT">Fee Discount</SelectItem>
                      <SelectItem value="ADJUSTMENT_INCREASE">Adjustment</SelectItem>
                      <SelectItem value="WRITE_OFF">Write-Off</SelectItem>
                      <SelectItem value="LAWYER_COLLECTION">Lawyer Collection</SelectItem>
                      <SelectItem value="LAWYER_FEE">Lawyer Fee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Change Status To</Label>
                  <Select value={bulkEditStatus} onValueChange={setBulkEditStatus}>
                    <SelectTrigger><SelectValue placeholder="Select status..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="successful">Successful</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
                <Button onClick={handleBulkEdit} disabled={!bulkEditType && !bulkEditStatus}>
                  Update {selectedTransactions.length} Transaction(s)
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default TransactionActions;
